import { Spin, message } from 'antd';
import React, { useEffect, useMemo } from 'react';

import Icon from '@ant-design/icons';
import { useGetBlockEvents, usePostBlockEvents, usePostUnblockEvents } from '@api/emp/queryHooks';
import BlockConfirm from '@components/block-event-single/components/BlockConfirm';
import BlockEventSingleRuleBuilder from '@components/block-event-single/components/BlockEventSingleRuleBuilder';
import PermissionButton from '@src/emp-3.0/components/PermissionButton';
import {
  BlockRuleTypeOptions,
  EBlockEventSingleChangeType,
  EBlockEventSingleRuleActionType,
  EVENT_NAME_WITH_BLOCK_RULE,
} from '@components/block-event-single/constant/constant';
import {
  EPanelOpenMethod,
  useBlockEventSinglePanelStore,
} from '@components/block-event-single/store';
import {
  BlockEventSingleWrapper,
  Header,
  HeaderTitleName,
  Wrapper,
} from '@components/block-event-single/styled';
import {
  addOrBlockRuleItem,
  deleteAllBlockRule,
  deleteBlockRuleItem,
  updateBlockRules,
} from '@components/block-event-single/utils/handleRulesUtils';
import {
  getDefaultBlockEventData,
  getDefaultBlockEventRuleItem,
} from '@components/block-event-single/utils/initializeUtils';
import { validateBlockEvent } from '@components/block-event-single/utils/validateUtils';
import BlockGreyIcon from '@icons/block-event/block-grey.svg';
import CancelIcon from '@icons/emp/cancel.svg';
import SaveIcon from '@icons/emp/save.svg';
import ButtonKit from '@components/common/ButtonKit';

const BlockEventSingle = () => {
  const [
    panelOpenMethod,
    blockType,
    blockEventsSingleDataGroup,
    setPanelOpenMethod,
    setBlockType,
    setBlockEventSingleOpen,
    setBlockEventsSingleDataGroup,
  ] = useBlockEventSinglePanelStore((state) => [
    state.panelOpenMethod,
    state.blockType,
    state.blockEventsSingleDataGroup,
    state.setPanelOpenMethod,
    state.setBlockType,
    state.setBlockEventSingleOpen,
    state.setBlockEventsSingleDataGroup,
  ]);

  const isCreateMode = panelOpenMethod === EPanelOpenMethod.CREATE;

  const {
    data: blockedEvents,
    isFetching: isBlockedEventsFetching,
    refetch: blockedEventsRefetch,
  } = useGetBlockEvents();
  const { mutate: blockEvents, isLoading: blockLoading } = usePostBlockEvents();
  const { mutate: unblockEvents, isLoading: unblockLoading } = usePostUnblockEvents();

  const eventNameSet = useMemo(() => {
    return blockEventsSingleDataGroup
      .map((events) => events?.event_name)
      .filter((name) => name !== undefined) as string[];
  }, [blockEventsSingleDataGroup]);

  useEffect(() => {
    if (
      !isBlockedEventsFetching &&
      eventNameSet?.[0] &&
      EVENT_NAME_WITH_BLOCK_RULE.includes(eventNameSet?.[0])
    ) {
      const findBlockEvent = (blockedEvents?.data ?? []).find((item) =>
        EVENT_NAME_WITH_BLOCK_RULE.includes(item.event_name),
      );
      // Update data only when block event(conviva_network_request) with block_rules.
      if (findBlockEvent && findBlockEvent?.block_rules?.length > 0) {
        setBlockEventsSingleDataGroup([findBlockEvent]);
        setBlockType(BlockRuleTypeOptions[1].value);
      }
    }
  }, [blockedEvents, isBlockedEventsFetching]);

  const handleResetStore = () => {
    setBlockType(BlockRuleTypeOptions[0].value);
    setBlockEventsSingleDataGroup([getDefaultBlockEventData()]);
    setPanelOpenMethod(EPanelOpenMethod.CREATE);
  };

  const handleUnblockEvent = () => {
    unblockEvents(
      { events: eventNameSet },
      {
        onSuccess: () => {
          message.success(`${eventNameSet.join()} has been unblocked from sensor.`).then();
          setBlockEventSingleOpen(false);
          handleResetStore();
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg).then();
        },
      },
    );
  };

  const handleBlockEvent = () => {
    let prepareBlockEventsData = blockEventsSingleDataGroup;
    // 'Block All' clears block_rules
    if (blockType === BlockRuleTypeOptions[0].value) {
      prepareBlockEventsData = blockEventsSingleDataGroup.map((data) => {
        const blockEventSingleData = { ...data };
        if (blockEventSingleData.block_rules.length !== 0) {
          blockEventSingleData.block_rules = [];
        }
        return blockEventSingleData;
      });
    }
    const res = validateBlockEvent(prepareBlockEventsData);
    if (!res.status) {
      message.warning(`${res.key?.join(' OR ')} ${res.result}`).then();
      return;
    }
    blockEvents(
      { events: res?.data },
      {
        onSuccess: () => {
          message.success(`${eventNameSet.join()} has been blocked from sensor.`).then();
          setBlockEventSingleOpen(false);
          handleResetStore();
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg).then();
        },
      },
    );
  };

  const handleCancel = () => {
    setBlockEventSingleOpen(false);
    handleResetStore();
  };

  const handleBlockTypeChange = (value, orIndex?: number) => {
    setBlockType(value);
    // After deleting all BlockRules,
    // a block Rule line is automatically added to the block_rules
    // when switching from 'Block All' to 'Block If...'.
    if (
      value === BlockRuleTypeOptions[1].value &&
      typeof orIndex === 'number' &&
      blockEventsSingleDataGroup[orIndex].block_rules.length === 0
    ) {
      blockEventsSingleDataGroup[orIndex].block_rules = [[getDefaultBlockEventRuleItem()]];
      setBlockEventsSingleDataGroup([...blockEventsSingleDataGroup]);
    }
  };

  const handleBlockRulesChange = (
    ruleType: EBlockEventSingleRuleActionType,
    orIndex: number,
    subAndIndex = 0,
    subOrIndex = 0,
  ) => {
    const newBlockEventsSingleDataGroup = blockEventsSingleDataGroup;
    switch (ruleType) {
      case EBlockEventSingleRuleActionType.DELETE_ALL:
        deleteAllBlockRule(newBlockEventsSingleDataGroup, orIndex);
        break;
      case EBlockEventSingleRuleActionType.DELETE_RULE_ITEM:
        deleteBlockRuleItem(newBlockEventsSingleDataGroup, orIndex, subAndIndex, subOrIndex);
        break;
      case EBlockEventSingleRuleActionType.ADD_OR_RULE_ITEM:
        addOrBlockRuleItem(newBlockEventsSingleDataGroup, orIndex);
        break;
      default:
        break;
    }
    setBlockEventsSingleDataGroup([...newBlockEventsSingleDataGroup]);
  };

  const handleBlockChange = (
    orIndex: number,
    subAndIndex: number | null,
    subOrIndex: number | null,
    changeType: EBlockEventSingleChangeType,
    value: string,
  ) => {
    const blockEventSingleData = blockEventsSingleDataGroup[orIndex];
    const isOnlySupportBlockAll =
      changeType === EBlockEventSingleChangeType.BLOCK_EVENT_SINGLE_NAME &&
      !EVENT_NAME_WITH_BLOCK_RULE.includes(value);
    if (isOnlySupportBlockAll) {
      handleBlockTypeChange(BlockRuleTypeOptions[0].value, orIndex);
      blockEventSingleData.block_rules = [];
    }
    if (EVENT_NAME_WITH_BLOCK_RULE.includes(value)) {
      blockedEventsRefetch();
    }
    setBlockEventsSingleDataGroup([
      ...updateBlockRules(
        blockEventsSingleDataGroup,
        orIndex,
        subAndIndex,
        subOrIndex,
        changeType,
        value,
      ),
    ]);
  };

  const blockConfirmTitle = (
    <div
      style={{
        fontWeight: 500,
        letterSpacing: '-0.14px',
        lineHeight: '20px',
        color: '#333',
      }}
    >
      Are you sure to block this event?
    </div>
  );

  return (
    <Spin spinning={isBlockedEventsFetching}>
      <Wrapper>
        <Header>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Icon component={BlockGreyIcon} style={{ fontSize: '16px' }} />
            <HeaderTitleName>Block Event</HeaderTitleName>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isCreateMode ? null : (
              <PermissionButton tooltip="" onClick={handleUnblockEvent}>
                <ButtonKit
                  kitType="danger"
                  onClick={handleUnblockEvent}
                  style={{
                    gap: '6px',
                    paddingRight: '11px',
                  }}
                  loading={blockLoading || unblockLoading}
                >
                  Unblock
                </ButtonKit>
              </PermissionButton>
            )}
            <PermissionButton tooltip="" styleType="default" onConfirm={handleBlockEvent}>
              <BlockConfirm title={blockConfirmTitle} showConfirm={isCreateMode}>
                <ButtonKit
                  kitType={isCreateMode ? 'danger' : 'primary'}
                  prefix={
                    <Icon component={SaveIcon} color={isCreateMode ? '#FF4D4F' : undefined} />
                  }
                  onClick={!isCreateMode ? handleBlockEvent : undefined}
                  style={{
                    gap: '6px',
                    paddingRight: '11px',
                  }}
                  loading={blockLoading || unblockLoading}
                >
                  {isCreateMode ? 'Block Event' : 'Update Block Event'}
                </ButtonKit>
              </BlockConfirm>
            </PermissionButton>
            <ButtonKit
              prefix={<Icon component={CancelIcon} />}
              onClick={handleCancel}
              style={{
                gap: '6px',
                paddingRight: '11px',
              }}
            >
              Cancel
            </ButtonKit>
          </div>
        </Header>
        <BlockEventSingleWrapper>
          <BlockEventSingleRuleBuilder
            mode={panelOpenMethod}
            data={blockEventsSingleDataGroup}
            blockType={blockType}
            onChange={handleBlockChange}
            onBlockRulesChange={handleBlockRulesChange}
            onBlockTypeChange={handleBlockTypeChange}
          />
        </BlockEventSingleWrapper>
      </Wrapper>
    </Spin>
  );
};

export default BlockEventSingle;
